import { Box, Flex, Grid, GridItem, SimpleGrid, Text, VisuallyHidden, Separator } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useCallback } from "react";

import { DownloadIcon } from "@digital-services-gard-as/anchor-icons";

import { useQuery } from "@tanstack/react-query";

import { Alert, Button, Heading } from "@digital-services-gard-as/anchor-react";
import { InvoiceSenderDto, getAttachment } from "@/api/invoiceApi";
import { getClaimRecipientDetailsQueryOptions } from "@/api/queryOptions/claimsApiQueryOptions";
import { getHierarchyQueryOption } from "@/api/queryOptions/usersAPIQueryOptions";
import { TruncatedText } from "@/components/TruncatedText";
import { createInvoiceAddressText } from "@/utils/addressUtils";
import { getTotalAmount } from "@/utils/costUtils";
import { formatBytes } from "@/utils/fileUtils";
import { StatusBadge } from "@/utils/invoiceUtils";
import { InvoiceDetailsDto, ClaimsDetailsDto, invoiceStatusValues } from "@/types";

type InvoiceReceiptProps = {
    invoice: InvoiceDetailsDto;
    invoiceSender?: InvoiceSenderDto;
    claim?: ClaimsDetailsDto;
    crmId?: string;
};

const FeesText = ({ label, value, testId }: { label: string; value: string; testId: string }) => {
    if (!value) {
        return null;
    }
    return (
        <Text data-testid={testId}>
            <b>{label}:</b> {value}
        </Text>
    );
};

const MAXIMUM_FRACTION_DIGITS = 2;
const MINIMUM_FRACTION_DIGITS = 2;
const InvoiceReceipt = ({ invoice, invoiceSender, claim, crmId }: InvoiceReceiptProps) => {
    const { t } = useTranslation("invoices");

    const { data: claimRecipient } = useQuery(getClaimRecipientDetailsQueryOptions(invoice.claimsRef || "", crmId));

    const { data: branchCompanies } = useQuery(getHierarchyQueryOption());
    const branchCompany = branchCompanies?.children?.find(
        branch => branch.crmId?.toString() === invoice.branchCompanyCrmId
    );

    const transformValueToCost = (value: number | undefined) => {
        if (!value) {
            return "";
        }
        const cost = value.toLocaleString("en-US", {
            maximumFractionDigits: MAXIMUM_FRACTION_DIGITS,
            minimumFractionDigits: MINIMUM_FRACTION_DIGITS
        });
        const currency = invoice.bankAccount?.currency;
        return `${cost} ${currency}`;
    };

    const correspondentCosts = transformValueToCost(invoice.correspondentCosts);
    const surveyorFees = transformValueToCost(invoice.surveyorCosts);
    const expertFees = transformValueToCost(invoice.expertCosts);
    const lawyerFees = transformValueToCost(invoice.lawyerFees);
    const lawyerExpenses = transformValueToCost(invoice.lawyerExpenses);
    const totalAmountVAT = transformValueToCost(invoice.totalVatAmount);
    const totalAmountLocalTaxes = transformValueToCost(invoice.totalLocalTax);
    const otherExpenses = transformValueToCost(invoice.otherExpenses);
    const totalAmount = transformValueToCost(getTotalAmount(invoice));
    const counselFees = transformValueToCost(invoice.counselFees);
    const arbitraryExpenses = transformValueToCost(invoice.courtArbitFees);
    const settlements = transformValueToCost(invoice.claimSettlements);
    const bankAccount = `${invoice.bankAccount?.accountNumber} (${invoice.bankAccount?.currency})`;
    const advanceOfFunds = transformValueToCost(invoice.advanceOfFunds);
    const previousAdvanceOfFunds = transformValueToCost(invoice.previousAdvanceOfFunds);

    const handleAttachmentClick = useCallback(() => {
        getAttachment(invoice.id, invoice.attachment?.documentId);
    }, [invoice.attachment?.documentId, invoice.id]);
    return (
        <Flex flexDir="column" justifyContent="center" gap="2.5rem" mb="5rem">
            <Flex flexDir="column" justifyContent="center">
                <Heading size="xl" style={{ textAlign: "center" }} id="createInvoiceTitle">
                    {t("invoiceReceipt.title")}
                </Heading>
                <Box mt="1rem" textAlign="center" id="invoiceStatus">
                    <InvoiceStatus invoice={invoice} />
                </Box>
            </Flex>
            <Flex flexDir="column" alignItems="center" gap="1rem">
                <Flex backgroundColor="white" flexDir="column" w={{ base: "100%", lg: "50rem" }} boxShadow={"md"}>
                    <Flex flexDir="column" p="2.5rem 1.5rem" justify="space-between">
                        <Heading size="lg" data-testid="submissionReceiptTitle">
                            {t("invoiceReceipt.submissionReceipt")}
                        </Heading>
                        <Grid templateRows="repeat(3, 1fr)" templateColumns="repeat(3, 1fr)" mt="1.5rem">
                            <GridItem>
                                <Text data-testid="submissionDate" asChild>
                                    <b>{t("invoiceReceipt.submissionDate")}</b>
                                </Text>
                                <Text data-testid="submissionDateVal">{invoice.submittedDate}</Text>
                            </GridItem>
                            <GridItem>
                                <Text data-testid="invoiceFrom" asChild>
                                    <b>{t("invoiceReceipt.invoiceFrom")}</b>
                                </Text>
                                <Text data-testid="invoiceFromVal">{invoiceSender?.name}</Text>
                            </GridItem>
                            <GridItem rowSpan={2}>
                                <Text data-testid="invoiceTo" asChild>
                                    <b>{t("invoiceReceipt.invoiceTo")}</b>
                                </Text>
                                <Text whiteSpace="pre-wrap" data-testid="invoiceToVal">
                                    {createInvoiceAddressText(claimRecipient)}
                                </Text>
                            </GridItem>
                            <GridItem>
                                <Text data-testid="invoiceDate" asChild>
                                    <b>{t("invoiceReceipt.invoiceDate")}</b>
                                </Text>
                                <Text data-testid="invoiceDateVal">{invoice.invoiceDate}</Text>
                            </GridItem>
                            <GridItem colStart={2}>
                                <Text data-testid="clientName" asChild>
                                    <b>{t("invoiceReceipt.clientName")}</b>
                                </Text>
                                <Text data-testid="clientNameVal">{claim?.clientName}</Text>
                            </GridItem>
                            <GridItem>
                                <Text data-testid="invoiceDueDate" asChild>
                                    <b>{t("invoiceReceipt.invoiceDueDate")}</b>
                                </Text>
                                <Text data-testid="invoiceDueDateVal">{invoice.invoiceDueDate}</Text>
                            </GridItem>
                            {branchCompany?.companyName && (
                                <GridItem colStart={2}>
                                    <Text data-testid="branchCompany" asChild>
                                        <b>{t("invoiceReceipt.branchCompany")}</b>
                                    </Text>
                                    <Text whiteSpace="pre-wrap" data-testid="branchCompanyVal" fontSize={14}>
                                        {branchCompany.companyName}
                                    </Text>
                                </GridItem>
                            )}
                        </Grid>
                    </Flex>
                    {/* Needs to be changed with anchor.navy.10 */}
                    <Flex backgroundColor="anchor.gray.50" flexDir="column" p="2.5rem 2rem" gap="0.5rem">
                        <Heading size="md">{claim?.description}</Heading>
                        <Text mt="1rem" data-testid="vessel">
                            <b>{t("invoiceReceipt.vessel")}:</b> {invoice.vesselName}
                        </Text>
                        <Text data-testid="incidentDate">
                            <b>{t("invoiceReceipt.incidentDate")}:</b> {invoice.incidentDate}
                        </Text>
                        <Text data-testid="subClaimsRef" whiteSpace="pre-wrap">
                            <b>{t("invoiceReceipt.subClaimsRef")}:</b> {invoice.subClaimsRef}
                        </Text>
                        <Text data-testid="yourInvoiceRef" whiteSpace="pre-wrap">
                            <b>{t("invoiceReceipt.yourInvoiceRef")}:</b> {invoice.invoiceNumber}
                        </Text>
                        <Text data-testid="gardClaimRef" whiteSpace="pre-wrap">
                            <b>{t("invoiceReceipt.gardClaimRef")}:</b> {invoice.claimsRef}
                        </Text>

                        <Separator colorPalette="blackAlpha" m="1rem" />
                        {/*eslint-disable-next-line no-magic-numbers*/}
                        <SimpleGrid columns={{ base: 1, lg: 3 }}>
                            <Flex flexDir="column" gap="0.5rem" whiteSpace="nowrap">
                                <Text data-testid="feesAndExpenses" asChild>
                                    <b>{t("invoiceReceipt.feesAndExpenses")}</b>
                                </Text>
                                <FeesText
                                    label={t("invoiceReceipt.correspondentFees")}
                                    value={correspondentCosts}
                                    testId="correspondentFees"
                                />
                                <FeesText
                                    label={t("invoiceReceipt.surveyorFees")}
                                    value={surveyorFees}
                                    testId="surveyorFees"
                                />
                                <FeesText
                                    label={t("invoiceReceipt.expertFees")}
                                    value={expertFees}
                                    testId="expertFees"
                                />
                                <FeesText
                                    label={t("invoiceReceipt.counselFees")}
                                    value={counselFees}
                                    testId="counselFees"
                                />
                                <FeesText
                                    label={t("invoiceReceipt.courtArbitFees")}
                                    value={arbitraryExpenses}
                                    testId="courtArbitFees"
                                />
                                <FeesText
                                    label={t("invoiceReceipt.claimSettlements")}
                                    value={settlements}
                                    testId="claimSettlements"
                                />
                                <FeesText
                                    label={t("invoiceReceipt.lawyerFees")}
                                    value={lawyerFees}
                                    testId="lawyerFees"
                                />
                                <FeesText
                                    label={t("invoiceReceipt.lawyerExpenses")}
                                    value={lawyerExpenses}
                                    testId="lawyerExpenses"
                                />
                                <FeesText
                                    label={t("invoiceReceipt.totalAmountVAT")}
                                    value={totalAmountVAT}
                                    testId="totalAmountVAT"
                                />
                                <FeesText
                                    label={t("invoiceReceipt.totalAmountLocalTaxes")}
                                    value={totalAmountLocalTaxes}
                                    testId="totalAmountLocalTaxes"
                                />
                                <FeesText
                                    label={t("invoiceReceipt.otherExpenses")}
                                    value={otherExpenses}
                                    testId="otherExpenses"
                                />
                                <FeesText
                                    label={t("invoiceReceipt.advanceOfFunds")}
                                    value={advanceOfFunds}
                                    testId="advanceOfFunds"
                                />
                                <FeesText
                                    label={t("invoiceReceipt.previousAdvanceOfFunds")}
                                    value={previousAdvanceOfFunds}
                                    testId="previousAdvanceOfFunds"
                                />
                            </Flex>
                            <Flex>
                                <VisuallyHidden />
                            </Flex>
                            <Flex flexDir="column" justifyContent="end" w="100%" h="100%" mt={{ base: "2rem", lg: 0 }}>
                                <Text data-testid="totalFeesExpensesTaxes">
                                    {t("invoiceReceipt.totalFeesExpensesTaxes")}:
                                </Text>
                                <Heading size="sm" data-testid="totalAmount">
                                    <b>{totalAmount}</b>
                                </Heading>
                                <Text mt="1rem" data-testid="toBankAccount" asChild>
                                    <b>{t("invoiceReceipt.toBankAccount")}</b>
                                </Text>
                                <Text data-testid="bankAccount">{bankAccount}</Text>
                            </Flex>
                        </SimpleGrid>
                    </Flex>
                </Flex>
                {invoice.attachment && (
                    <Flex justifyContent="center" gap={3} mt="3rem" flexDir="column" w="100%" maxW="50rem">
                        <Heading size="lg" data-testid="attachments">
                            {t("invoiceReceipt.attachments")}:
                        </Heading>
                        <Flex
                            gap="0.5rem"
                            alignItems="center"
                            _hover={{ color: "blue" }}
                            w="100%"
                            justify="space-between"
                            bg="anchor.white"
                            p="1rem"
                            rounded="lg"
                            border="2px solid"
                            borderColor="anchor.brown.100"
                        >
                            <Flex gap="1rem" data-testid="filename" alignItems="center">
                                <Flex>
                                    <TruncatedText
                                        text={`${invoice.attachment?.filename}.${invoice.attachment?.fileExtension}`}
                                    />
                                </Flex>
                                <Text>{formatBytes(invoice.attachment?.fileSize)}</Text>
                            </Flex>
                            <Button
                                variant="secondary"
                                aria-label={t("ariaLabels.downloadAttachment") + invoice.attachment?.filename}
                                onClick={handleAttachmentClick}
                                icon={<DownloadIcon />}
                            />
                        </Flex>
                    </Flex>
                )}
            </Flex>
        </Flex>
    );
};

export default InvoiceReceipt;

const InvoiceStatus = ({ invoice }: { invoice: InvoiceDetailsDto }) => {
    const { t } = useTranslation("invoices");
    const rejectedReason = invoice.rejectedReason?.trim();

    if (invoice.status === invoiceStatusValues.Rejected && rejectedReason) {
        return (
            <Flex justify="center" textAlign="left" pt="2rem">
                <Box w={{ base: "100%", lg: "50rem" }}>
                    <Alert variant="danger" contentMaxWidth={false} data-testid="invoiceRejectedAlert">
                        <Flex flexDir="column" gap="0.25rem">
                            <Text fontWeight="bold">{t("invoiceReceipt.rejected")}</Text>
                            <Text>{`${t("invoiceReceipt.rejectedReason")}: ${rejectedReason}`}</Text>
                        </Flex>
                    </Alert>
                </Box>
            </Flex>
        );
    }

    return (
        <Box>
            <StatusBadge id="statusBadge" invoiceStatus={invoice.status} />
        </Box>
    );
};
